import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BellRing,
  CalendarDays,
  CheckCircle2,
  GraduationCap,
  MessageSquare,
  Smartphone,
} from 'lucide-react';
import { Button, Logo, StripeBand } from '@/design/components';
import { useSettings } from '@/access/SettingsContext';
import { isValidPhone, normalizePhone } from '@/lib/phone';
import { supabase } from '@/lib/supabase';
import { Turnstile } from './Turnstile';

/**
 * טופס ההרשמה הציבורי (PRD 5.17).
 *
 * ארבעה שלבים ולא טופס אחד ארוך. הראשון הוא ברוכים הבאים בלי שום
 * שדה: זו הפנייה הראשונה של הרשת לאדם הזה, והיא לא אמורה להיראות
 * כמו טופס במשרד.
 *
 * ת"ז, פרטי בנק וחוזה אינם כאן בכוונה. ראו את ההערה למטה.
 *
 * המסך לא נוגע בשום טבלה. כל השמירה דרך submit-application.
 */
type Step = 'welcome' | 'details' | 'code' | 'done';

type Details = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  birth_date: string;
  city: string;
  street: string;
  house_number: string;
  apartment: string;
  note: string;
};

const EMPTY: Details = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  birth_date: '',
  city: '',
  street: '',
  house_number: '',
  apartment: '',
  note: '',
};

type FunctionResult = {
  ok: boolean;
  error?: { code: string; message_he: string };
};

export function JoinPage() {
  const { token = '' } = useParams();
  const { brand, appUrl } = useSettings();

  const [step, setStep] = useState<Step>('welcome');
  const [details, setDetails] = useState<Details>(EMPTY);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const set = (name: keyof Details) => (value: string) =>
    setDetails((prev) => ({ ...prev, [name]: value }));

  const onToken = useCallback((value: string | null) => setTurnstileToken(value), []);

  const canStart =
    details.first_name.trim() !== '' &&
    details.last_name.trim() !== '' &&
    isValidPhone(details.phone) &&
    turnstileToken !== null;

  async function call(payload: Record<string, unknown>): Promise<FunctionResult> {
    const { data, error: failure } = await supabase.functions.invoke<FunctionResult>(
      'submit-application',
      { body: payload },
    );
    if (failure) {
      // שגיאת רשת, או תשובה שאינה 2xx. הגוף עדיין מכיל הודעה בעברית
      const body = (failure as { context?: { json?: () => Promise<FunctionResult> } }).context;
      if (body?.json) {
        try {
          return await body.json();
        } catch {
          /* ממשיכים לנוסח הכללי */
        }
      }
      return { ok: false, error: { code: 'network', message_he: 'אין חיבור. נסו שוב.' } };
    }
    return data ?? { ok: false, error: { code: 'empty', message_he: 'משהו השתבש. נסו שוב.' } };
  }

  async function sendCode() {
    setError(null);
    setIsBusy(true);
    const result = await call({
      action: 'start',
      token,
      phone: details.phone,
      turnstileToken,
    });
    setIsBusy(false);

    if (result.ok) setStep('code');
    else setError(result.error?.message_he ?? 'משהו השתבש. נסו שוב.');
  }

  async function submit() {
    setError(null);
    setIsBusy(true);
    const result = await call({
      action: 'verify',
      token,
      phone: details.phone,
      code: code.trim(),
      first_name: details.first_name,
      last_name: details.last_name,
      email: details.email,
      birth_date: details.birth_date,
      city: details.city,
      street: details.street,
      house_number: details.house_number,
      apartment: details.apartment,
      note: details.note,
    });
    setIsBusy(false);

    if (result.ok) setStep('done');
    else setError(result.error?.message_he ?? 'משהו השתבש. נסו שוב.');
  }

  return (
    <div className="grid min-h-dvh grid-rows-[auto_1fr] bg-surface">
      <div className="bg-charcoal">
        <div className="flex h-32 items-center justify-center">
          <Logo on="charcoal" height={36} />
        </div>
        <StripeBand />
      </div>

      <main className="mx-auto grid w-full max-w-md content-start gap-6 p-4 pt-8">
        <Steps current={step} />

        {step === 'welcome' && (
          <Welcome appTitle={brand.app_title} onStart={() => setStep('details')} />
        )}

        {step === 'details' && (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void sendCode();
            }}
          >
            <div className="grid gap-2">
              <h1 className="m-0 font-display text-display-l font-semibold">הפרטים שלך</h1>
              <p className="m-0 text-body text-muted">
                רק מה שצריך כדי לפתוח לך כרטיס עובד/ת. בסוף נשלח קוד אימות לטלפון.
              </p>
            </div>

            <Text label="שם פרטי" required value={details.first_name} onChange={set('first_name')} />
            <Text label="שם משפחה" required value={details.last_name} onChange={set('last_name')} />

            <PhoneField value={details.phone} onChange={set('phone')} />

            <Text
              label="מייל"
              dir="ltr"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={details.email}
              onChange={set('email')}
            />
            <Text
              label="תאריך לידה"
              type="date"
              dir="ltr"
              value={details.birth_date}
              onChange={set('birth_date')}
            />

            <fieldset className="m-0 grid gap-3 rounded-md border border-line p-3">
              <legend className="px-1 text-label font-medium">כתובת</legend>
              <Text label="עיר" value={details.city} onChange={set('city')} />
              <Text label="רחוב" value={details.street} onChange={set('street')} />
              <div className="grid grid-cols-2 gap-3">
                <Text
                  label="מספר בית"
                  inputMode="numeric"
                  value={details.house_number}
                  onChange={set('house_number')}
                />
                <Text
                  label="מספר דירה"
                  inputMode="numeric"
                  value={details.apartment}
                  onChange={set('apartment')}
                />
              </div>
            </fieldset>

            <Text label="הערה למנהל/ת" value={details.note} onChange={set('note')} />

            <Turnstile onToken={onToken} />

            {error && (
              <p role="alert" className="m-0 text-body-sm text-bad">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" block disabled={isBusy || !canStart}>
              {isBusy ? 'שולח' : 'שלחו לי קוד אימות'}
            </Button>

            <Button variant="ghost" onClick={() => setStep('welcome')} className="px-0">
              חזרה
            </Button>

            <p className="m-0 text-body-sm text-muted">
              תעודת זהות, פרטי בנק וחוזה העבודה ימתינו לך באפליקציה אחרי האישור, במקום מוגן.
            </p>
          </form>
        )}

        {step === 'code' && (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className="grid gap-2">
              <h1 className="m-0 font-display text-display-l font-semibold">הזינו את הקוד</h1>
              <p className="m-0 text-body text-muted">
                שלחנו קוד בן 6 ספרות ל{' '}
                <span dir="ltr" className="tabular">
                  {details.phone}
                </span>
                . הקוד תקף לעשר דקות.
              </p>
            </div>

            <div className="grid gap-1">
              <label htmlFor="join-code" className="text-label font-medium">
                קוד אימות
              </label>
              <input
                id="join-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-center font-display text-display-m tabular"
              />
              <p className="m-0 text-body-sm text-muted">
                {code.length < 6
                  ? `עוד ${6 - code.length} ספרות`
                  : 'אפשר לשלוח'}
              </p>
            </div>

            {error && (
              <p role="alert" className="m-0 text-body-sm text-bad">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" block disabled={isBusy || code.length < 6}>
              {isBusy ? 'בודק' : 'שליחת הבקשה'}
            </Button>

            <Button variant="ghost" onClick={() => setStep('details')} className="px-0">
              חזרה לתיקון הפרטים
            </Button>

            <p className="m-0 text-body-sm text-muted">
              לא הגיע קוד? כדאי לבדוק שהמספר נכון, ולחזור אחורה לתקן אותו.
            </p>
          </form>
        )}

        {step === 'done' && (
          <Done appTitle={brand.app_title} firstName={details.first_name} appUrl={appUrl} />
        )}
      </main>
    </div>
  );
}

/**
 * ברוכים הבאים: בלי שדות בכלל.
 *
 * מי שסורק QR בסניף לא יודע מה הוא פותח. מסך שמתחיל ב"שם פרטי"
 * מניח שהוא כבר יודע, ומוכר הרשמה כמו טופס. כאן קודם אומרים מה
 * זה, ורק אז מבקשים.
 */
function Welcome({ appTitle, onStart }: { appTitle: string; onStart: () => void }) {
  return (
    <div className="grid gap-5">
      <div className="grid gap-3">
        <h1 className="m-0 font-display text-display-l font-semibold">
          ברוכים הבאים למשפחת {appTitle}
        </h1>
        <p className="m-0 text-body text-ink">
          שמחים שהצטרפת אלינו. אנחנו רשת שבנויה על האנשים שעומדים בחנויות, ומכאן מתחיל החלק
          שלך: כמה דקות של פרטים, ואנחנו פותחים לך כרטיס עובד/ת ומקום קבוע בצוות. נעים
          מאוד, ובהצלחה.
        </p>
        <p className="m-0 text-body text-ink">
          ההרשמה היא בשלושה שלבים קצרים: ממלאים פרטים אישיים, מאמתים את מספר הטלפון בקוד
          שנשלח ב SMS, והבקשה עוברת למנהל/ת הסניף לאישור. אחרי האישור תקבל/י הודעת SMS עם
          קישור לאפליקציה — שם מחכים סידור העבודה, הגשת אילוצים, הודעות הצוות, הלומדות
          והמסמכים האישיים.
        </p>
      </div>

      <ul className="m-0 grid list-none gap-2 p-0">
        <Highlight icon={CalendarDays}>הסידור שלך, ובקשות להחלפת משמרת</Highlight>
        <Highlight icon={MessageSquare}>הודעות מהסניף ומההנהלה, במקום אחד</Highlight>
        <Highlight icon={GraduationCap}>לומדות קצרות והיכרות עם המוצרים</Highlight>
        <Highlight icon={BellRing}>התראה כשמשהו דורש את תשומת הלב שלך</Highlight>
      </ul>

      <Button variant="primary" block onClick={onStart}>
        מתחילים ברישום
      </Button>

      <p className="m-0 text-body-sm text-muted">ההרשמה לוקחת כשתי דקות.</p>
    </div>
  );
}

function Highlight({
  icon: Icon,
  children,
}: {
  icon: typeof CalendarDays;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 rounded-md border border-line bg-raised p-3">
      <Icon size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-muted" />
      <span className="text-body text-ink">{children}</span>
    </li>
  );
}

/**
 * סיום: מה קורה עכשיו, ואיך נכנסים.
 *
 * קודם היה כאן משפט אחד. מי שסיים הרשמה הוא בדיוק מי שלא יודע
 * שאין מה לחפש בחנות האפליקציות, ושהכניסה היא בלי סיסמה.
 */
function Done({
  appTitle,
  firstName,
  appUrl,
}: {
  appTitle: string;
  firstName: string;
  appUrl: string;
}) {
  return (
    <div className="grid gap-5">
      <div className="grid gap-3">
        <h1 className="m-0 flex items-center gap-2 font-display text-display-l font-semibold">
          <CheckCircle2 size={28} strokeWidth={1.75} aria-hidden="true" className="text-good" />
          הבקשה נשלחה
        </h1>
        <p className="m-0 text-body text-ink">
          תודה{firstName.trim() ? ` ${firstName.trim()}` : ''}. מנהל/ת הסניף מקבל/ת התראה
          עכשיו, ואחרי האישור תגיע אליך הודעת SMS עם קישור ל{appTitle}.
        </p>
      </div>

      <div className="grid gap-3 rounded-md border border-line bg-raised p-4">
        <h2 className="m-0 flex items-center gap-2 font-display text-display-s font-semibold">
          <Smartphone size={20} strokeWidth={1.75} aria-hidden="true" />
          איך נכנסים, כשהקישור מגיע
        </h2>
        <ol className="m-0 grid list-decimal gap-2 ps-5 text-body text-ink">
          <li>פותחים את הקישור מה SMS בטלפון. אין מה לחפש בחנות האפליקציות.</li>
          <li>
            מקלידים את אותו מספר טלפון שרשמת כאן, ומקבלים קוד כניסה ב SMS. אין סיסמה לזכור.
          </li>
          <li>
            בדף תופיע הצעה להוסיף את {appTitle} למסך הבית. כדאי — ככה היא נפתחת כמו אפליקציה
            רגילה ומקבלת התראות.
          </li>
        </ol>
        <p className="m-0 text-body-sm text-muted">
          באייפון מוסיפים דרך כפתור השיתוף בספארי, ואז "הוספה למסך הבית".
        </p>
      </div>

      {/*
        הכתובת עצמה, ולא רק "יישלח קישור": מי שסגר את ה SMS או
        מחק אותו נשאר בלי שום דרך להגיע. היא לא תעבוד עד האישור,
        וזה כתוב.
      */}
      <div className="grid gap-2 rounded-md border border-line p-4">
        <span className="text-label font-medium">הכתובת, לשמירה</span>
        <a href={appUrl} dir="ltr" className="break-all text-body text-ink">
          {appUrl}
        </a>
        <p className="m-0 text-body-sm text-muted">
          הכניסה תעבוד אחרי שמנהל/ת הסניף יאשר/תאשר את הבקשה.{' '}
          <a href={`${appUrl}/install`} className="text-ink">
            הסבר על ההתקנה והכניסה
          </a>
          .
        </p>
      </div>

      <p className="m-0 text-body-sm text-muted">אפשר לסגור את הדף.</p>
    </div>
  );
}

function Steps({ current }: { current: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'welcome', label: 'ברוכים הבאים' },
    { key: 'details', label: 'פרטים' },
    { key: 'code', label: 'אימות' },
    { key: 'done', label: 'סיום' },
  ];
  const index = steps.findIndex((step) => step.key === current);

  return (
    <ol className="m-0 flex list-none gap-2 p-0" aria-label="שלבי ההרשמה">
      {steps.map((step, position) => (
        <li
          key={step.key}
          aria-current={position === index ? 'step' : undefined}
          className={`flex-1 border-t-2 pt-2 text-label ${
            position <= index ? 'border-olive text-ink' : 'border-line text-muted'
          }`}
        >
          {step.label}
        </li>
      ))}
    </ol>
  );
}

/**
 * טלפון ישראלי: עשר ספרות.
 *
 * המונה אומר כמה נשאר, ולא "המספר אינו תקין" אחרי שסיימו להקליד.
 * שגיאה שמופיעה בסוף מחייבת לחזור ולספור ספרות; מונה שרץ תוך כדי
 * פשוט לא נותן לטעות.
 */
function PhoneField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const digits = value.replace(/\D/g, '');
  const left = 10 - digits.length;
  const valid = isValidPhone(value);

  const hint = () => {
    if (digits.length === 0) return 'עשר ספרות, כמו 0501234567';
    if (left > 0) return `עוד ${left} ספרות`;
    if (!valid) return 'המספר לא נראה תקין. מספר ישראלי מתחיל ב 0';
    return `יישלח קוד ל ${normalizePhone(value)}`;
  };

  return (
    <div className="grid gap-1">
      <label htmlFor="join-phone" className="text-label font-medium">
        טלפון
        <span aria-hidden="true"> *</span>
      </label>
      <input
        id="join-phone"
        type="tel"
        dir="ltr"
        inputMode="numeric"
        autoComplete="tel"
        maxLength={10}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 10))}
        aria-describedby="join-phone-hint"
        className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
      />
      <p
        id="join-phone-hint"
        className={`m-0 text-body-sm ${digits.length === 10 && !valid ? 'text-bad' : 'text-muted'}`}
      >
        {hint()}
      </p>
    </div>
  );
}

function Text({
  label,
  value,
  onChange,
  required,
  dir,
  type = 'text',
  inputMode,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  dir?: 'ltr';
  type?: string;
  inputMode?: 'numeric' | 'email';
  autoComplete?: string;
}) {
  const id = `join-${label}`;
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        dir={dir}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
      />
    </div>
  );
}
